import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Button, EmptyState, Stack } from '@relay-towing/ui';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import type { AuthErrorKind } from '../../utils/authErrorKind';
import { SELECTED_COMPANY_KEY } from '../../hooks/userCompanies.shared';

interface AuthErrorScreenProps {
  kind: AuthErrorKind;
  onLogout?: () => void;
  onRetry?: () => void;
  errorMessage?: string;
}

const NO_COMPANY_DEFAULT =
  'Please contact your company admin for more details on your account creation.';

// One screen for both auth dead ends, so they cannot be confused again.
// Support cannot fix an expired token, so that line stays off the expired state.
const AuthErrorScreen: React.FC<AuthErrorScreenProps> = ({
  kind,
  onLogout,
  onRetry,
  errorMessage,
}) => {
  const { logout } = useHybridAuth();

  // In embedded mode logout() posts signOut and the shell re-runs its own login.
  const handleReturnToLogin = () => {
    // A 403 can come from a stale company pick: the x-company-id header is
    // read from this key on every request and nothing else ever clears it, so
    // a driver removed from that company would sign in and land straight back
    // here. Dropping the pick lets the next sign in fall back to the default.
    if (kind === 'no-company') {
      localStorage.removeItem(SELECTED_COMPANY_KEY);
    }
    if (onLogout) {
      onLogout();
    } else {
      logout();
    }
  };

  const isExpired = kind === 'session-expired';

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
      <EmptyState
        icon={AlertCircle}
        title={
          isExpired ? 'Your session expired' : 'No company found for your user'
        }
        description={
          isExpired
            ? 'Sign in again to pick up where you left off.'
            : `${errorMessage || NO_COMPANY_DEFAULT} If there is more assistance needed please contact support@relaytow.com`
        }
        action={
          <Stack gap={2} className="w-full max-w-xs">
            <Button fullWidth onClick={handleReturnToLogin}>
              {isExpired ? 'Sign in' : 'Return to Log in'}
            </Button>
            {onRetry && (
              <Button variant="outline" fullWidth onClick={onRetry}>
                Try again
              </Button>
            )}
          </Stack>
        }
      />
    </div>
  );
};

export default AuthErrorScreen;
